import { Component, Inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { VerseReference } from '@models/VerseReference';
import { UbsBook } from '@models/UbsBook';
import { MatSelectModule } from '@angular/material/select';
import { ProjectConfiguration } from '@models/ProjectConfiguration';
import { SynchronizerService } from '../../synchronizer.service';
import { CanonData, getCanon } from '@models/Canons';

@Component({
  selector: 'app-verse-reference-dialog',
  imports: [MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatSelectModule,
    ReactiveFormsModule],
  templateUrl: './verse-reference-dialog.component.html',
  styleUrl: './verse-reference-dialog.component.scss'
})
export class VerseReferenceDialogComponent {
  form: FormGroup;

  constructor(private formBuilder: FormBuilder,
    private synchronizerService: SynchronizerService,
    public dialogRef: MatDialogRef<VerseReferenceDialogComponent, VerseReference>,
    @Inject(MAT_DIALOG_DATA) public reference: VerseReference) {

    this.form = this.formBuilder.group({
      canon: new FormControl(this.reference.canon),
      book: new FormControl(this.reference.ubs_book),
      chapter: [this.reference.chapter, [Validators.min(1), Validators.pattern(/^\d+$/)]],
      verse: [this.reference.verse, [Validators.min(1), Validators.pattern(/^\d+$/)]]
    });

    this.form.get('book')?.valueChanges.subscribe(() => this.bookChanged());
    this.form.get('canon')?.valueChanges.subscribe(() => this.canonChanged());

    this.setValidators();
  }

  static maxValidator(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value > max) {
        control.setValue(max);
      }
      return null;
    };
  }

  /// convenience
  get project(): ProjectConfiguration | undefined {
    return this.synchronizerService.currentProject;
  }

  get maxChapter(): number {
    const book = this.form.get('book')?.value as UbsBook;
    return this.canonData?.lastChapter(book) || -1;
  }

  get maxVerse(): number {
    const book = this.form.get('book')?.value as UbsBook;
    const chapter = this.form.get('chapter')?.value as number;
    return this.canonData?.lastVerse(book, chapter) || -1;
  }

  get bookCodes(): UbsBook[] {
    const canon = this.form.get('canon')?.value;
    if (canon == null) { return []; }
    return this.canonData?.books || [];
  }

  get canonData(): CanonData | undefined {
    const canon = this.form.get('canon')?.value;
    if (canon == null) { return undefined; }
    return getCanon(canon);
  }

  bookName(book: UbsBook): string {
    return this.project?.getBookName(book) || "";
  }

  bookChanged() {
    this.form.get('chapter')?.setValue(1);
    this.form.get('verse')?.setValue(1);
    this.setValidators();
  }

  setValidators() {
    this.form.get('chapter')?.setValidators([Validators.min(1), VerseReferenceDialogComponent.maxValidator(this.maxChapter)]);
    this.form.get('chapter')?.updateValueAndValidity();
    this.form.get('verse')?.setValidators([Validators.min(1), VerseReferenceDialogComponent.maxValidator(this.maxVerse)]);
    this.form.get('verse')?.updateValueAndValidity();
  }

  canonChanged() {
    const canon = this.form.get('canon')?.value;
    const project_id = this.synchronizerService.currentProject?.id;
    if (!canon || !project_id) { return; }
    const newReference = this.synchronizerService.currentPosition?.getReferenceFor(project_id, canon);
    if (newReference) {
      this.form.get('book')?.setValue(newReference.ubs_book);
      this.form.get('chapter')?.setValue(newReference.chapter);
      this.form.get('verse')?.setValue(newReference.verse);
    } else {
      this.form.get('book')?.setValue(this.bookCodes[0]);
      this.form.get('chapter')?.setValue(1);
      this.form.get('verse')?.setValue(1);
    }
  }

  chapterChanged() {
    this.form.get('verse')?.setValue(1);
    this.form.get('verse')?.setValidators([Validators.min(1), VerseReferenceDialogComponent.maxValidator(this.maxVerse)]);
    this.form.get('verse')?.updateValueAndValidity();
  }

  dismiss() {
    this.dialogRef.close();
  }

  go() {
    if (this.canonData) {
      const book = this.form.get('book')?.value as UbsBook;
      const chapter = this.form.get('chapter')?.value as number;
      const verse = this.form.get('verse')?.value as number;
      this.dialogRef.close(new VerseReference(book, chapter, verse, this.canonData.name));
    }
  }
}
